import { forwardRef, useEffect, useImperativeHandle } from "react";

const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function loadScript() {
  if (window.grecaptcha && window.grecaptcha.execute) return Promise.resolve();
  if (window.__recaptchaPromise) return window.__recaptchaPromise;
  window.__recaptchaPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      const wait = () => (window.grecaptcha && window.grecaptcha.execute
        ? window.grecaptcha.ready(() => resolve())
        : setTimeout(wait, 50));
      wait();
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.__recaptchaPromise;
}

// Google reCAPTCHA v3 (invisible, score-based). Call `ref.current.execute()` on submit to
// get a fresh single-use token for the given action; the badge is injected globally by Google.
export const Recaptcha = forwardRef(function Recaptcha({ action = "submit" }, ref) {
  // Load on mount so the badge renders and the first submit isn't stalled on script latency.
  useEffect(() => { loadScript().catch((e) => console.error("[recaptcha] script load failed", e)); }, []);

  useImperativeHandle(ref, () => ({
    execute: async () => {
      try {
        await loadScript();
        return await window.grecaptcha.execute(siteKey, { action });
      } catch (e) {
        // Surface the real cause (e.g. "Invalid site key or not loaded in api.js") for diagnosis.
        console.error("[recaptcha] execute failed", e);
        return null;
      }
    },
  }), [action]);

  return <div data-testid="recaptcha-widget" className="hidden" aria-hidden="true" />;
});
